// UploadModal.tsx
import React from "react";
import { createPortal } from "react-dom";
import { FileUpload } from "../FileUpload/FileUpload";

export interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: any) => void;
  onReject?: (rejectedFiles: any) => void;
  title?: string;
  accept?: string[];
  maxSize?: number;
  multiple?: boolean;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  onReject,
  title = "Upload Document",
  accept = [
    ".pdf",
    ".doc",
    ".docx",
    ".txt",
    ".jpg",
    ".png",
    ".gif",
    ".xls",
    ".xlsx",
  ],
  maxSize = 10 * 1024 * 1024, // 10MB
  multiple = false,
}) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  // Reset selected file when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
    }
  }, [isOpen]);

  const handleFileUpload = (file: any) => {
    // Handle the file from FileUpload component
    if (file && file.file) {
      setSelectedFile(file.file);
    } else if (file instanceof File) {
      setSelectedFile(file);
    }
  };

  const handleConfirmUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile);
      onClose();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255,255,255,0)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          minWidth: "400px",
          maxWidth: "500px",
          border:"1px solid #ccc",
          boxShadow:"0 5px 15px #808080"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 15px 0" }}>{title}</h3>

        <div style={{ marginBottom: "15px" }}>
          <FileUpload
            config={{
              accept,
              maxSize,
              multiple,
              disabled: false,
            }}
            placeholder="Drop files here or click to browse"
            onUpload={handleFileUpload}
            onReject={onReject}
            style={{
              border: "2px dashed #ccc",
              borderRadius: "4px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
            }}
          />
        </div>

        {/* Selected File Details */}
        {selectedFile && (
          <div
            style={{
              padding: "4px 0px",
            }}
          >
            <div style={{ fontSize: "14px", marginBottom: "5px" }}>
              Selected File: {selectedFile.name}
            </div>
          </div>
        )}

        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              cursor: "pointer",
              border: "1px solid #ccc",
              backgroundColor: "white",
              borderRadius: "4px",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmUpload}
            disabled={!selectedFile}
            style={{
              padding: "8px 16px",
              cursor: selectedFile ? "pointer" : "not-allowed",
              border: selectedFile ? "1px solid rgb(0, 141, 206)" : "#ccc",
              backgroundColor: selectedFile ? "rgb(0, 141, 206)" : "#ccc",
              color: "white",
              borderRadius: "4px",
            }}
          >
            Upload
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
